<template>
  <div class="metrics-chart-card" data-testid="metrics-class-performance-card">
    <div class="metrics-chart-card__header">
      <div class="metrics-chart-card__header-left">
        <span class="metrics-chart-card__title heading-s">{{
          $t("metrics.classPerformanceTrends")
        }}</span>
        <eg-icon
          icon="information"
          class="metrics-chart-card__info-icon"
          @click.native="
            $eventBus.$emit('onboarding:clicked:metricsClassPerformanceHint')
          "
        />
      </div>
      <eg-dropdown
        ref="dropdown"
        :text="selectedPeriodLabel"
        class="metrics-chart-card__period-filter"
      >
        <eg-b-dropdown-item-button
          v-for="option in periodOptions"
          :key="option.value"
          @click="$emit('update:selectedPeriod', option.value)"
        >
          {{ option.label }}
        </eg-b-dropdown-item-button>
      </eg-dropdown>
    </div>

    <p class="metrics-chart-card__subtitle paragraph-m">
      {{ $t("metrics.numberOfStudents") }}
    </p>

    <div ref="chartBody" class="metrics-chart-card__body">
      <infinity-loading v-if="isLoading && !hasData" />
      <chart-j-s-bar
        v-else-if="hasData"
        :chart-data="chartData"
        :chart-options="chartOptions"
        :plugins="chartPlugins"
        width="100%"
        :height="260"
      />
      <metrics-empty-state v-else />
      <div
        v-show="tooltipState.visible"
        class="perf-chart-tooltip"
        :style="{
          left: tooltipState.left + 'px',
          top: tooltipState.top + 'px',
        }"
      >
        <p class="perf-chart-tooltip__title paragraph-s">
          {{ tooltipState.title }}
        </p>
        <div
          v-for="ds in tooltipDatasets"
          :key="ds.label"
          class="perf-chart-tooltip__row"
        >
          <span
            class="perf-chart-tooltip__dot"
            :style="{ background: ds.color }"
          />
          <span class="perf-chart-tooltip__text paragraph-s">
            <strong>{{ ds.value }} {{ $t("metrics.students") }}</strong>
          </span>
        </div>
      </div>
    </div>

    <div v-if="hasData" class="metrics-chart-card__legend">
      <div
        v-for="item in chartLegend"
        :key="item.label"
        class="metrics-chart-card__legend-item"
      >
        <span
          class="metrics-chart-card__legend-dot"
          :style="{ background: item.color }"
        />
        <span class="metrics-chart-card__legend-subtitle paragraph-s">{{
          item.subtitle
        }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import ChartJSBar from "@/components/global/chartjs/chart_js_bar.vue";
import { stackTotalsPlugin } from "@/components/global/chartjs/chart_js_plugins";
import MetricsEmptyState from "../shared/metrics_empty_state.vue";
import InfinityLoading from "@/components/rubrics/newRubric/components/infinity-loading.vue";

import {
  TIER_COLORS,
  formatTooltipTitle,
} from "./metrics_class_performance_chart.constants";
import dropdownHideOnLeaveMixin from "@/mixins/dropdownHideOnLeaveMixin";

export default {
  name: "MetricsClassPerformanceChart",

  components: {
    ChartJSBar,
    MetricsEmptyState,
    InfinityLoading,
  },

  mixins: [dropdownHideOnLeaveMixin],

  props: {
    data: {
      type: Object,
      default: () => ({ labels: [], datasets: [] }),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    selectedPeriod: {
      type: String,
      default: "month",
    },
  },

  data() {
    return {
      chartPlugins: [stackTotalsPlugin],
      tooltipState: {
        visible: false,
        title: "",
        colIdx: null,
        left: 0,
        top: 0,
      },
    };
  },

  computed: {
    hasData() {
      if (!this.data.labels?.length) return false;
      return (
        this.data.datasets?.some((ds) =>
          ds.data?.some((v) => v != null && v !== 0)
        ) ?? false
      );
    },

    trimmedData() {
      const labels = this.data.labels || [];
      const datasets = this.data.datasets || [];
      if (!labels.length) return { labels: [], datasets: [] };

      const keepIndices = labels
        .map((_, i) => i)
        .filter((i) =>
          datasets.some((ds) => ds.data[i] != null && ds.data[i] !== 0)
        );

      return {
        labels: keepIndices.map((i) => labels[i]),
        datasets: datasets.map((ds) => ({
          ...ds,
          data: keepIndices.map((i) => ds.data[i] ?? 0),
        })),
      };
    },

    tooltipDatasets() {
      const { colIdx } = this.tooltipState;
      if (colIdx === null) return [];
      return (this.trimmedData.datasets || []).map((ds) => ({
        label: ds.label,
        value: ds.data[colIdx] || 0,
        color: TIER_COLORS[ds.label] || "#94A3B8",
      }));
    },

    periodOptions() {
      return [
        { value: "day", label: this.$t("metrics.periodDay") },
        { value: "week", label: this.$t("metrics.periodWeek") },
        { value: "month", label: this.$t("metrics.periodMonth") },
      ];
    },

    tierSubtitles() {
      return {
        Strong: this.$t("metrics.tierStudentsGte", { min: 85 }),
        Growing: this.$t("metrics.tierStudentsRange", { min: 65, max: 84 }),
        "Need Support": this.$t("metrics.tierStudentsLt", { max: 65 }),
      };
    },

    chartLegend() {
      return Object.keys(this.tierSubtitles).map((label) => ({
        label,
        color: TIER_COLORS[label],
        subtitle: this.tierSubtitles[label],
      }));
    },

    selectedPeriodLabel() {
      const found = this.periodOptions.find(
        (o) => o.value === this.selectedPeriod
      );
      return found ? found.label : "";
    },

    yAxisMax() {
      const { datasets, labels } = this.trimmedData;
      if (!datasets.length) return 10;
      let maxSum = 0;
      labels.forEach((_, i) => {
        const sum = datasets.reduce((acc, ds) => acc + (ds.data[i] || 0), 0);
        if (sum > maxSum) maxSum = sum;
      });
      return Math.ceil(maxSum / 10) * 10 * 1.1 || 10;
    },

    yAxisStepSize() {
      if (this.yAxisMax <= 10) return 1;
      if (this.yAxisMax <= 30) return 3;
      return 5;
    },

    chartData() {
      const datasets = this.trimmedData.datasets || [];
      return {
        labels: this.trimmedData.labels,
        datasets: datasets.map((ds, index) => ({
          label: ds.label,
          data: ds.data,
          backgroundColor: TIER_COLORS[ds.label] || "#94A3B8",
          borderRadius:
            index === datasets.length - 1 ? { topLeft: 3, topRight: 3 } : 0,
          borderSkipped: "bottom",
          borderWidth:
            index < datasets.length - 1
              ? { top: 2, right: 0, bottom: 0, left: 0 }
              : 0,
          borderColor: "#fff",
          barPercentage: 0.45,
        })),
      };
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 8 } },
        onHover(event, elements) {
          event.native.target.style.cursor = elements.length
            ? "pointer"
            : "default";
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            mode: "index",
            intersect: true,
            external: this.buildTooltip,
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: "#566370",
              font: {
                family: "Inter",
                size: 12,
                weight: "400",
                lineHeight: 1.2,
              },
            },
          },
          y: {
            stacked: true,
            max: this.yAxisMax,
            grid: { color: "#F0F3F6" },
            border: { display: false },
            ticks: {
              color: "#566370",
              font: {
                family: "Inter",
                size: 12,
                weight: "400",
                lineHeight: 1.2,
              },
              stepSize: this.yAxisStepSize,
            },
          },
        },
      };
    },
  },

  methods: {
    buildTooltip(context) {
      const { chart, tooltip } = context;

      if (tooltip.opacity === 0 || !chart.getActiveElements().length) {
        this.tooltipState.visible = false;
        return;
      }

      const colIdx = tooltip.dataPoints?.[0]?.dataIndex;
      const totalY = (tooltip.dataPoints || []).reduce(
        (sum, dp) => sum + (dp.parsed.y || 0),
        0
      );
      const stackTopPixel = chart.scales.y.getPixelForValue(totalY);
      const canvasRect = chart.canvas.getBoundingClientRect();
      const bodyRect = this.$refs.chartBody.getBoundingClientRect();

      this.tooltipState = {
        visible: true,
        title: formatTooltipTitle(
          tooltip.title?.[0] || "",
          this.selectedPeriod
        ),
        colIdx,
        left: canvasRect.left - bodyRect.left + tooltip.caretX,
        top: canvasRect.top - bodyRect.top + stackTopPixel,
      };
    },
  },
};
</script>

<style lang="scss">
.perf-chart-tooltip {
  position: absolute;
  pointer-events: none;
  transform: translate(-50%, calc(-100% - 10px));
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 12px 14px;
  font-family: Inter, sans-serif;
  min-width: 210px;
  z-index: 10;

  &__title.paragraph-s {
    margin: 0 0 8px;
    font-weight: var(--typography-font-weight-fw-600, 600);
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__dot {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    border-radius: 4px;
    display: inline-block;
  }

  &__text span {
    color: var(--color-text-neutral-secondary, #566370);
  }
  &__text strong {
    font-weight: var(--typography-font-weight-fw-600, 600);
  }
}
</style>

<style lang="scss" scoped>
@import "../shared/metrics_chart_card";

.metrics-chart-card {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin: 0;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__title {
    font-weight: var(--typography-font-weight-fw-700, 700);
    color: var(--color-text-neutral-on-white);
  }

  &__subtitle {
    color: var(--color-text-neutral-fade);
    margin: 0;
    font-weight: var(--typography-font-weight-fw-400);
  }

  &__period-filter {
    @include metrics-dropdown;
    ::v-deep .btn {
      padding: 4px 10px;
    }
  }

  &__body {
    position: relative;
    min-height: 260px;
  }

  &__legend {
    display: flex;
    justify-content: center;
    gap: 20px;
    padding-top: 12px;
    flex-wrap: wrap;
  }

  &__legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__legend-dot {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    border-radius: 4px;
  }

  &__legend-subtitle {
    color: var(--color-text-neutral-fade);
    font-weight: var(--typography-font-weight-fw-600, 600);
  }
}
</style>
